<template>
	<div ref="poComponent" class="poComponent" ispopover>
		<div @click="showPopover">
			<slot />
		</div>

		<transition name="fade">
			<div
				v-if="isOpen"
				ref="popover"
				class="__popover"
				:class="[hPos, vPos]"
				:style="hvStyle"
				ispopover
				@click="togglePopover">
				<slot name="popover" />
			</div>
		</transition>
	</div>
</template>

<script setup lang="ts">
	import { ref, computed, nextTick, watch, onBeforeUnmount } from 'vue'

	/** Props */
	const props = defineProps({
		forceTop: { type: Boolean, default: false },
		disable: { type: Boolean, default: false },
	})

	/** Refs to DOM elements */
	const poComponent = ref<HTMLElement | null>(null)
	const popover = ref<HTMLElement | null>(null)

	/** Reactive state */
	const isOpen = ref(false)
	const hPos = ref<'_left' | '_right'>('_right')
	const vPos = ref<'_top' | '_bottom'>('_top')
	const topStyle = ref(0)
	const leftStyle = ref(0)

	/** Computed style object */
	const hvStyle = computed(() => ({
		top: `${topStyle.value}px`,
		left: `${leftStyle.value}px`,
	}))

	watch(
		() => props.disable,
		(newVal) => {
			if (newVal) {
				isOpen.value = false
			}
		},
	)
	/** Show and position popover */
	function showPopover() {
		isOpen.value = true
		nextTick(() => {
			if (!poComponent.value || !popover.value) return

			const tb = poComponent.value.getBoundingClientRect()
			const pb = popover.value.getBoundingClientRect()
			const vw = window.innerWidth
			const vh = window.innerHeight

			// Horizontal placement
			if (tb.right + pb.width < vw) {
				leftStyle.value = tb.right
				hPos.value = '_left'
			} else {
				leftStyle.value = tb.left - pb.width
				hPos.value = '_right'
			}

			// Vertical placement
			if (props.forceTop || tb.bottom + pb.height > vh) {
				topStyle.value = tb.top - pb.height
				vPos.value = '_top'
			} else {
				topStyle.value = tb.bottom
				vPos.value = '_bottom'
			}
		})
	}

	/** Toggle popover when clicking inside the trigger */
	function togglePopover(e: MouseEvent) {
		// Prevent closing when clicking the popover itself (checked by ispopover attr)
		if (!(e.target as HTMLElement).hasAttribute('ispopover')) {
			isOpen.value = !isOpen.value
		}
	}

	/** Global click handler: if click is outside the component, close it */
	function handleClickOutside(e: MouseEvent) {
		const target = e.target as Node
		// If click is neither on the trigger nor inside the popover, close
		if (poComponent.value && !poComponent.value.contains(target)) {
			isOpen.value = false
		}
	}

	/** Clean up on unmount */
	onBeforeUnmount(() => {
		document.removeEventListener('click', handleClickOutside)
		window.removeEventListener('resize', handleClickOutside)
	})

	/** Watch open state: add/remove global click & resize listeners */
	watch(isOpen, (open) => {
		if (open) {
			document.addEventListener('click', handleClickOutside)
			window.addEventListener('resize', handleClickOutside)
		} else {
			document.removeEventListener('click', handleClickOutside)
			window.removeEventListener('resize', handleClickOutside)
		}
	})
</script>

<style scoped>
	@reference '@/styles/tailwind.css';

	.poComponent {
		display: inline-block;
		vertical-align: bottom;
		position: relative;
	}

	.__popover {
		@apply bg-base-300 border-base-content/40 fixed z-10 rounded border p-0 shadow-lg;
	}

	.fade-enter-active,
	.fade-leave-active {
		transition: opacity 0.1s ease 60ms;
	}
	.fade-enter-from,
	.fade-leave-to {
		opacity: 0;
		transform: translateY(0);
		transition-delay: 0ms;
	}
</style>
